import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DataTable } from '../../components/Tables';
import PageHeader from '../shared/PageHeader';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { courtCasesApi, extractList } from '../../api/facades';

const CourtCasesListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [clientId, setClientId] = useState<number | ''>('');
  const [priority, setPriority] = useState<'High' | 'Medium' | 'Low' | ''>('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { data: res, isLoading } = useQuery({
    queryKey: ['court-cases', search, clientId, priority],
    queryFn: () =>
      courtCasesApi.list({
        search: search || undefined,
        clientId: typeof clientId === 'number' ? clientId : undefined,
        priority: priority || undefined,
      }),
  });
  const rows = extractList(res);

  const deleteMu = useMutation({
    mutationFn: (id: number) => courtCasesApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['court-cases'] });
      setDeleteId(null);
      toast.success('Court case deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  const columns = [
    { key: 'case_number', label: 'Case No.', render: (r: any) => r.case_number ?? '—' },
    { key: 'party_name', label: 'Party' },
    { key: 'priority', label: 'Priority' },
    { key: 'date_of_service', label: 'Date of service', render: (r: any) => (r.date_of_service ? String(r.date_of_service).slice(0, 10) : '—') },
    { key: 'case_status', label: 'Case status', render: (r: any) => r.case_status?.case_status_name ?? '—' },
  ];

  return (
    <div>
      <PageHeader hideTitle subtitle="Manage court cases." primaryAction={{ label: 'New Court Case', to: '/dashboard/court-cases/new' }} />
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <div className="mb-4 flex flex-wrap gap-4">
          <input type="number" placeholder="Client ID" value={clientId === '' ? '' : clientId} onChange={(e) => setClientId(e.target.value === '' ? '' : Number(e.target.value))} className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm w-28" />
        <select value={priority} onChange={(e) => setPriority(e.target.value as 'High' | 'Medium' | 'Low' | '')} className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm">
          <option value="">All</option>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </select>
          <input type="search" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm w-48" />
        </div>
        <DataTable columns={columns} data={rows} loading={isLoading} emptyMessage="No court cases." onRowClick={(id) => navigate(`/dashboard/court-cases/${id}`)} onEdit={(id) => navigate(`/dashboard/court-cases/${id}/edit`)} onDelete={(id) => setDeleteId(Number(id))} />
      </div>
      <ConfirmDeleteModal open={deleteId != null} onClose={() => setDeleteId(null)} onConfirm={() => deleteId != null && deleteMu.mutate(deleteId)} title="Delete court case" message="Are you sure? This cannot be undone." loading={deleteMu.isPending} />
    </div>
  );
};

export default CourtCasesListPage;
